import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    CircularProgress, Box, Typography
} from '@mui/material';
import { FocusContext, useFocusable, init } from '@noriginmedia/norigin-spatial-navigation';
import { DialogButton } from './dialogButton';
import { useHref, useNavigate } from 'react-router-dom';
import { getPlansByRegion, upateSubscriberPackage, getStripeCheckoutSession, capturePaypalOrder, getSubscriber } from '../../../Api/api';
import { ANALYTICS_EVENTS, LOCAL_STORAGE_KEY, PLAYER } from '../../../constant';
import { utility } from '../../../utility/utility';
import PaymentProviderSelectionWeb from './PaymentProviderSelectionWeb';
import PlanStatusDialogWeb from './PlanStatusDialog';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import AnalyticsManager from '../../../analytics/analyticsManager';

// Initialize spatial navigation
init({
    debug: false,
    visualDebug: false,
});

const PlanSelectionDialog = ({ openPlanDialog = true }) => {

    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [paymentProvider, setPaymentProvider] = useState(false);
    const [openPlanStatusDialog, setOpenPlanStatusDialog] = useState({ open: false, status: "success", plan: null });
    const navigate = useNavigate();
    const user = utility.getValue(LOCAL_STORAGE_KEY.user);

    const onExitDialog = () => {
        navigate(-1);
    }

    const { ref: mainRef, focusKey: mainFocusKey, focusSelf: focusMain } = useFocusable({
        focusable: true,
        saveLastFocusedChild: true,
        trackChildren: true,
        autoRestoreFocus: true,
        isFocusBoundary: true,
        focusKey: "plans-list",
    });

    const { ref: dialogRef, focusKey: dialogFocusKey, focusSelf: focusDialog } = useFocusable({
        focusable: true,
        saveLastFocusedChild: true,
        trackChildren: true,
        autoRestoreFocus: true,
        isFocusBoundary: true,
        focusKey: "dialog-container",
        preferredChildFocusKey: "dialog-ok-button",
    });

    useEffect(() => {
        const hash = window.location.hash;
        const queryString = hash.includes("?") ? hash.split("?")[1] : "";
        const queryParams = new URLSearchParams(queryString);

        const status = queryParams.get("status");
        const sessionId = queryParams.get("session_id");
        const token = queryParams.get("token");

        if (status === "success" && token) {
            updatePaypalPlan(token);
        }
        if (status === "success" && sessionId) {
            updateStripePlan(sessionId);
        } else if (status === "failure") {
            setOpenPlanStatusDialog({ open: true, status: "failure" });
        }
        const newHash = hash.split("?")[0];
        window.location.hash = newHash;
    }, [window.location.hash]);

    const updateStripePlan = async (sessionId) => {
        try {
            const res = await getStripeCheckoutSession(sessionId);

            if (res.success) {
                const session = res.data;

                const response = await upateSubscriberPackage({ Subscriber_id: user?._id, Package_id: session?.planId, orderId: sessionId, paymentId: session?.payment_intent });

                if (response.success) {
                    let subscriber = await getSubscriber(user?.RMN);
                    if (subscriber.success) {
                        utility.setValue(LOCAL_STORAGE_KEY.user, subscriber.data);
                    }
                    setOpenPlanStatusDialog({ open: true, status: "success", plan: session.planName });
                } else {
                    toast.error(response.message);
                }
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            console.error("Error fetching session:", error);
            toast.error(error.message);
        }
    };

    const updatePaypalPlan = async (orderId) => {
        try {
            const response = await capturePaypalOrder(orderId);

            if (response.success) {
                const captureDetails = response?.capture?.result;
                const paymentId = captureDetails?.purchase_units[0]?.payments?.captures[0]?.id;
                const planId = captureDetails?.purchase_units[0]?.reference_id;

                const res = await upateSubscriberPackage({
                    Subscriber_id: user?._id,
                    Package_id: planId,
                    orderId: orderId,
                    paymentId: paymentId,
                });
                if (res.success) {
                    let subscriber = await getSubscriber(user?.RMN);
                    if (subscriber.success) {
                        utility.setValue(LOCAL_STORAGE_KEY.user, subscriber.data);
                    }
                    const planName = plans.find(plan => plan._id === planId)?.Description;
                    setOpenPlanStatusDialog({ open: true, status: "success", plan: planName });
                } else {
                    toast.error(res.message);
                }
            } else {
                setOpenPlanStatusDialog({ open: true, status: "failure" });
            }
        } catch (error) {
            console.error("Error capturing PayPal order:", error);
            setOpenPlanStatusDialog({ open: true, status: "failure" });
        }
    };

    useEffect(() => {
        if (openPlanDialog) {
            loadPlans();
        }
    }, [openPlanDialog]);

    useEffect(() => {
        if (openPlanDialog && !openConfirmDialog) {
            focusMain();
        } else if (openConfirmDialog) {
            focusDialog();
        }
    }, [openPlanDialog, openConfirmDialog, plans]);

    useEffect(() => {
        document.addEventListener('keydown', onBack);
        return () => {
            document.removeEventListener('keydown', onBack);
        }
    }, []);

    const loadPlans = async () => {
        let user = utility.getValue(LOCAL_STORAGE_KEY.user);
        if (!user) {
            return;
        }
        setIsLoading(true);
        let res = await getPlansByRegion();
        console.log(res);
        if (res.success) {
            const planData = res.data.map((d, i) => {
                return {
                    id: i,
                    ...d
                }
            });
            setPlans(planData);
            // Set the first plan as initially selected
            if (planData.length > 0) {
                setSelectedPlan(planData[0]);
            }
        } else {
            setPlans([]);
            console.log(res);
        }
        setIsLoading(false);
    };

    const onBack = (e) => {
        const { keyCode } = e;

        if (keyCode === PLAYER.VK_BACK_SPACE || keyCode === PLAYER.VK_BACK || keyCode === PLAYER.BACK_LG || keyCode === PLAYER.RETURN) {
            e.preventDefault();
            if (openConfirmDialog) {
                setOpenConfirmDialog(false);
            } else {
                onExitDialog();
            }
        }
    }

    const selectPlan = (plan) => {
        setSelectedPlan(plan);
    };

    const confirmPlan = () => {
        if (selectedPlan.PaymentProviders?.length > 0) {
            setPaymentProvider(true);
        }
    };

    const closeConfirmDialog = () => {
        setOpenConfirmDialog(false);
        focusMain();
    };

    const handlePaymentSuccess = async (res) => {
        let response = await upateSubscriberPackage({ Subscriber_id: user?._id, Package_id: res?.plan?._id, orderId: res?.razorpay_order_id, paymentId: res?.razorpay_payment_id });
        if (response.success) {
            let subscriber = await getSubscriber(user?.RMN);
            if (subscriber.success) {
                utility.setValue(LOCAL_STORAGE_KEY.user, subscriber.data);
            }
            // Analytics event
            AnalyticsManager().logEvent(ANALYTICS_EVENTS.PAYMENT_SUCCESS, {...user, Subscriber_id: user?._id, Package_id: res?.plan?._id, orderId: res?.razorpay_order_id, paymentId: res?.razorpay_payment_id });
            setOpenPlanStatusDialog({ open: true, status: "success", plan: res?.plan?.Description });
        } else {
            toast.error(response.message);
        }
    }

    const handlePaymentFailure = (msg) => {
        setOpenPlanStatusDialog({ open: true, status: "failure" });
        // Analytics event
        AnalyticsManager().logEvent(ANALYTICS_EVENTS.PAYMENT_FAILURE, {...user, message: msg});
    }

    const PlanCard = ({ plan, isSelected = false }) => {
        const { ref, focused } = useFocusable({
            focusKey: `plan-${plan.id}`,
            onEnterPress: () => selectPlan(plan),
            onArrowPress: (direction) => {
                const element = document.getElementById(`plan-${plan.id}`);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                return true;
            }
        });

        const cardStyle = {
            position: 'relative',
            background: isSelected ? 'linear-gradient(135deg, #4a9eff, #0066cc)' :
                (focused ? '#3a3a3a' : '#2a2c35'),
            border: isSelected ? '2px solid #4a9eff' :
                (focused ? '2px solid #666' : '1px solid #444'),
            borderRadius: '8px',
            padding: '16px',
            color: 'white',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '80px'
        };

        return (
            <div
                id={`plan-${plan.id}`}
                ref={ref}
                style={cardStyle}
                className="plan-card"
                onClick={() => selectPlan(plan)}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* <div
                        style={{
                            background: isSelected ? 'rgba(255, 255, 255, 0.2)' : '#1a1c24',
                            borderRadius: '12px',
                            width: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <i className="fa fa-star" style={{ fontSize: '20px', color: '#e20074' }}></i>
                    </div> */}

                    <div style={{ flex: 1 }}>
                        <Typography variant="h6" style={{
                            fontSize: '16px',
                            fontWeight: 'bold',
                            margin: '0 0 4px 0',
                            color: isSelected ? 'white' : '#ffa726'
                        }}>
                            {plan.Description}
                        </Typography>

                        <div style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '4px',
                            marginBottom: '4px'
                        }}>
                            <Typography variant="h6" style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                color: 'white'
                            }}>
                                ₹{plan.Amount}
                            </Typography>
                            <Typography variant="body2" style={{
                                fontSize: '12px',
                                color: isSelected ? 'rgba(255,255,255,0.8)' : '#999'
                            }}>
                                /{plan.OttPeriodDays} days
                            </Typography>
                        </div>

                        <Typography variant="body2" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: isSelected ? 'rgba(255,255,255,0.9)' : '#999',
                            fontSize: '11px'
                        }}>
                            <span style={{ color: '#4caf50' }}>✓</span>
                            Device Limit: {plan.deviceLimit} Devices
                        </Typography>

                        {(plan.Providers || []).length > 1 && <Typography variant="body2" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: isSelected ? 'rgba(255,255,255,0.9)' : '#999',
                            fontSize: '11px'
                        }}>
                            {(plan.Providers || []).length} Partner Apps
                        </Typography>}

                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '8px',
                                flexWrap: 'nowrap',
                                overflowX: 'auto'
                            }}
                        >
                            {(plan.Providers || []).map((pvdr, idx) => (
                                <img
                                    key={idx}
                                    src={pvdr.LogoUrl}
                                    alt="provider-icon"
                                    style={{
                                        width: '28px',
                                        height: '28px',
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                        background: '#fff'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '16px'
                }}>
                    {isSelected ? (
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <span style={{
                                background: 'white',
                                color: '#4a9eff',
                                borderRadius: '50%',
                                width: '14px',
                                height: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px'
                            }}>
                                ✓
                            </span>
                            Selected
                        </div>
                    ) : (
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                            Select
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <Dialog
                open={openPlanDialog}
                onClose={() => onExitDialog()}
                aria-labelledby="plan-dialog-title"
                aria-describedby="plan-dialog-description"
                PaperProps={{
                    style: {
                        backgroundColor: "#1a1c24",
                        color: "white",
                        borderRadius: "12px",
                        position: "relative",
                        minWidth: "500px",
                        maxWidth: "600px",
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column"
                    },
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={() => onExitDialog()}
                    sx={{ position: "absolute", top: 10, right: 10, color: "white", zIndex: 1 }}
                >
                    <i className="fa fa-times" style={{ fontSize: "20px" }}></i>
                </IconButton>

                <FocusContext.Provider value={mainFocusKey}>
                    <Box ref={mainRef} style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        minHeight: "400px"
                    }}>
                        {/* Title */}
                        <DialogTitle id="plan-dialog-title" sx={{ textAlign: "center", paddingTop: "40px", paddingBottom: "16px" }}>
                            <Typography variant="h6" fontWeight="bold">
                                Choose Your Plan
                            </Typography>
                            <Typography variant="body2" style={{
                                color: '#999',
                                marginTop: '4px',
                                fontSize: '12px'
                            }}>
                                Select a plan that suits your needs
                            </Typography>
                        </DialogTitle>

                        {/* Scrollable Plans List */}
                        <DialogContent style={{
                            flex: 1,
                            overflow: "auto",
                            padding: "0 20px",
                            minHeight: "200px"
                        }}>
                            {isLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "24px", color: "#e20074" }}></i>
                                </Box>
                            ) : plans.length === 0 ? (
                                <Box textAlign="center" pt={4}>
                                    <div style={{ display: "flex", justifyContent: "center", paddingBottom: "10px" }}>
                                        <div style={{
                                            width: 60,
                                            height: 60,
                                            backgroundColor: '#e20074',
                                            color: "white",
                                            borderRadius: '50%',
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}>
                                            <i className="fa fa-star" style={{ fontSize: "24px" }}></i>
                                        </div>
                                    </div>
                                    <Typography variant="h6" style={{ color: '#ccc' }}>No plans found</Typography>
                                </Box>
                            ) : (
                                <div>
                                    {plans.map((plan) => (
                                        <PlanCard
                                            key={plan.id}
                                            plan={plan}
                                            isSelected={selectedPlan && selectedPlan.id === plan.id}
                                        />
                                    ))}
                                </div>
                            )}
                        </DialogContent>

                        {/* Fixed Upgrade Button Row */}
                        <div style={{
                            background: 'linear-gradient(45deg, #4a9eff, #0066cc)',
                            padding: '16px 20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTop: '1px solid #333',
                            minHeight: '70px',
                            borderRadius: '0 0 12px 12px'
                        }}>
                            <div>
                                <Typography variant="body2" style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>
                                    You pay
                                </Typography>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '8px'
                                }}>
                                    <Typography variant="h6" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                        ₹{selectedPlan ? selectedPlan.Amount : 0}
                                    </Typography>
                                    {selectedPlan && (
                                        <Typography variant="body2" style={{
                                            fontSize: '12px',
                                            opacity: 0.8
                                        }}>
                                            for {selectedPlan.OttPeriodDays} days
                                        </Typography>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={confirmPlan}
                                disabled={!selectedPlan}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: selectedPlan ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: selectedPlan ? 1 : 0.5,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Upgrade Now →
                            </button>
                        </div>
                    </Box>
                </FocusContext.Provider>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={openConfirmDialog}
                onClose={closeConfirmDialog}
                aria-labelledby="confirm-dialog-title"
                PaperProps={{
                    style: {
                        backgroundColor: "#1a1c24",
                        color: "white",
                        padding: "20px",
                        borderRadius: "12px",
                        position: "relative",
                    },
                }}
            >
                <IconButton
                    onClick={closeConfirmDialog}
                    sx={{ position: "absolute", top: 10, right: 10, color: "white" }}
                >
                    <i className="fa fa-times" style={{ fontSize: "20px" }}></i>
                </IconButton>

                <FocusContext.Provider value={dialogFocusKey}>
                    <Box ref={dialogRef}>
                        <DialogTitle id="confirm-dialog-title" sx={{ textAlign: "center", marginTop: "20px" }}>
                            <Typography variant="h6" fontWeight="bold">
                                {selectedPlan ? `Upgrade to ${selectedPlan.Description}` : 'Complete Payment'}
                            </Typography>
                        </DialogTitle>
                        <DialogContent style={{ color: '#ccc', textAlign: 'center' }}>
                            {selectedPlan ? (
                                <div>
                                    <Typography variant="body1" style={{ marginBottom: '8px' }}>
                                        You have selected: <strong>{selectedPlan.Description}</strong>
                                    </Typography>
                                    <Typography variant="body1" style={{ marginBottom: '8px' }}>
                                        Amount: <strong>₹{selectedPlan.Amount}</strong> for {selectedPlan.OttPeriodDays} days
                                    </Typography>
                                    <Typography variant="body1" style={{ marginBottom: '16px' }}>
                                        Device Limit: <strong>{selectedPlan.deviceLimit} Devices</strong>
                                    </Typography>
                                    <Typography variant="body2" style={{ color: '#999' }}>
                                        Please complete payment on your mobile device
                                    </Typography>
                                </div>
                            ) : (
                                <Typography variant="body1">
                                    Please complete payment on your mobile device
                                </Typography>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', paddingTop: '20px' }}>
                            <DialogButton
                                onClick={() => navigate('/')}
                                focusKey="dialog-ok-button"
                                style={{
                                    background: 'linear-gradient(45deg, #4a9eff, #0066cc)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 24px',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}
                            >
                                OK
                            </DialogButton>
                        </DialogActions>
                    </Box>
                </FocusContext.Provider>
            </Dialog>

            {paymentProvider && <PaymentProviderSelectionWeb
                open={paymentProvider}
                onClose={() => setPaymentProvider(false)}
                paymentProviders={selectedPlan?.PaymentProviders}
                selectedPlan={selectedPlan}
                onPaymentSuccess={(res) => handlePaymentSuccess(res)}
                onPaymentFailure={(res) => handlePaymentFailure(res)}
            />}

            <PlanStatusDialogWeb
                open={openPlanStatusDialog.open}
                onClose={() => {
                    if (openPlanStatusDialog.status === "success") {
                        setOpenPlanStatusDialog({ open: false, status: "success" });
                        navigate("/");
                    } else {
                        setOpenPlanStatusDialog({ open: false, status: "success" });
                    }
                }}
                status={openPlanStatusDialog.status}
                planName={openPlanStatusDialog.plan}
            />

            <style jsx>{`
                .plan-card:hover {
                    transform: translateX(2px);
                }
                
                /* Custom scrollbar */
                ::-webkit-scrollbar {
                    width: 6px;
                }
                
                ::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 3px;
                }
                
                ::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 3px;
                }
                
                ::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
            `}</style>
        </>
    );
};

export default PlanSelectionDialog;